import { useMutation, useQuery } from '@apollo/client';
import {
  Generator,
  getTranslatedText,
  Loader,
  toast,
  HeaderTag,
  AccessControl
} from '@nile/components';
import { RMMClient } from '@nile/graphql';
import TicketContext from '@nile/mangifera/src/Contexts/TicketContext';
import {
  GET_NON_MONITORED_ASSET_META_INFO_LIST_V3,
  REMOVE_ASSOCIATION
} from '@nile/rmm/src/modules/asset-management/Graphql/asset';
import AssociatedAssetParent from '@nile/rmm/src/modules/it-documentation/components/sub-pages/associated-asset';
import { Popover, Modal, Button } from '@superops/ui-design-system';
import gql from 'graphql-tag';
import { cloneDeep, isEmpty, map } from 'lodash';
import React, { useCallback, useContext, useEffect, useState } from 'react';
import { FormattedMessage, injectIntl } from 'react-intl';
import { withRouter } from 'react-router-dom';
import NonMonitoredAssetCreate from '@nile/rmm/src/modules/it-documentation/components/pages/non-monitored-asset-create/index';
import {
  ActionsIconBlock,
  AssetClassActionsPopover,
  AssociatedAssetClassParent
} from './styles';
import { SVGIcon } from '@superops/super-icons';

function AssociatedAssetClass(props) {
  const {
    assetClass,
    assetId,
    refetchListType,
    setRefetchListType,
    intl
  } = props;
  const [assetClassPopover, setAssetClassPopover] = useState(false);
  const [assets, setAssets] = useState([]);
  const [loader, setLoader] = useState(true);
  const [showITDocPopup, setShowITDocPopup] = useState(false);

  const getAssetVariables = () => {
    const variables = {
      assetId: {
        type: 'ID',
        required: true
      },
      associatedEntityName: {
        type: 'String'
      },
      listInfo: {
        type: 'ListInfoInput'
      }
    };
    return variables;
  };

  const getTableData = async () => {
    const generator = new Generator({
      operation: 'getNonMonitoredAssociatedAssets',
      variables: getAssetVariables(),
      fields: [
        {
          listInfo: ['page', 'pageSize', 'hasMore', 'totalCount']
        },
        {
          assets: ['assetId', 'name']
        }
      ]
    });

    const queryString = generator.queryBuilder();
    const response = await RMMClient.query({
      query: gql`
        ${queryString.query}
      `,
      variables: {
        assetId,
        associatedEntityName: assetClass.entityName,
        listInfo: {
          pageSize: 20,
          page: 1
        }
      },
      fetchPolicy: 'network-only'
    });
    setAssets(response.data.getNonMonitoredAssociatedAssets.assets);
    setLoader(false);
    if (refetchListType) setRefetchListType(null);
  };
  useEffect(() => {
    getTableData();
  }, [assetId]);

  useEffect(() => {
    if (refetchListType === assetClass?.name) {
      getTableData();
    }
  }, [refetchListType]);

  const toggleAssetClassActionsPopoverActive = useCallback(() => {
    setAssetClassPopover(!assetClassPopover);
  });

  const assetClassActionsActivator = (
    <AccessControl
      roleToCheck={{
        operator: 'OR',
        roles: ['itDocumentation.add', 'itDocumentation.edit']
      }}
      render={
        <Button
          id={'associateAssetClass'}
          secondary
          onClick={() => toggleAssetClassActionsPopoverActive()}
        >
          <SVGIcon size="sm" name="Actions" />
        </Button>
      }
    />
  );

  const [removeAssociation] = useMutation(REMOVE_ASSOCIATION, {
    client: RMMClient,
    onCompleted() {
      toast('success', 'asset.details.itDoc.associateRemoved.success');
    }
  });

  const handleRemoveAssociation = (ids) => {
    const processedIds = ids.map((id) => {
      return id.toString();
    });
    const { entityName } = props.assetClass;
    removeAssociation({
      variables: {
        assetId: props.match.params?.assetId || props.match.params?.id,
        relatedAsset: {
          entityName,
          assetIds: processedIds
        }
      }
    }).then((res) => {
      if (res?.data?.removeAssociation) {
        const clonedAssets = cloneDeep(assets);
        const updatedAssets = clonedAssets.filter((asset) => {
          return processedIds.includes(asset.assetId.toString()) === false;
        });
        if (assetClassPopover) {
          toggleAssetClassActionsPopoverActive();
        }
        setAssets(updatedAssets);
      }
    });
  };

  const toggleITDocPopup = () => {
    setShowITDocPopup((prev) => !prev);
    toggleAssetClassActionsPopoverActive();
  };
  const createNew = getTranslatedText(intl, 'asset.details.itDoc.createNew');
  const associate = getTranslatedText(intl, 'asset.details.itDoc.associate');
  return (
    <AssociatedAssetClassParent>
      <div className="fx actr">
        <div className="asset-class-name flex-item-balloon">
          <HeaderTag isNewLayout title={props?.assetClass?.name}></HeaderTag>
        </div>
        <Popover
          activator={assetClassActionsActivator}
          active={assetClassPopover}
          onClose={() => {
            toggleAssetClassActionsPopoverActive();
          }}
        >
          <AssetClassActionsPopover>
            <div className="tab-name-block">
              <AccessControl
                roleToCheck="itDocumentation.add"
                render={
                  <div className="action-block" onClick={toggleITDocPopup}>
                    <div className="action-name">{`${createNew} ${props?.assetClass?.name}`}</div>
                  </div>
                }
              />

              <AccessControl
                roleToCheck="itDocumentation.edit"
                render={
                  <>
                    <div
                      className="action-block"
                      onClick={props.showAssetListToAssociate}
                    >
                      {`${associate} ${props?.assetClass?.name}`}
                    </div>
                    <div
                      className="action-block"
                      onClick={() => {
                        handleRemoveAssociation(map(assets, 'assetId'));
                        toggleAssetClassActionsPopoverActive();
                      }}
                    >
                      <div className="delete-action-name">
                        <FormattedMessage id="common.remove" />
                      </div>
                    </div>
                  </>
                }
              />
            </div>
          </AssetClassActionsPopover>
        </Popover>
      </div>
      <div className="asset-list">
        <div className="asset-block-parent">
          {!isEmpty(assets) ? (
            assets.map((asset) => {
              return (
                <AssociatedAssetParent
                  asset={asset}
                  assetClass={props?.assetClass}
                  removeAssociation={handleRemoveAssociation}
                />
              );
            })
          ) : (
            <>
              {loader ? (
                <Loader />
              ) : (
                <span>
                  {getTranslatedText(
                    props.intl,
                    'asset.details.itdocumentation.no.associated.assets'
                  )}
                </span>
              )}
            </>
          )}
        </div>
      </div>
      <Modal
        title=""
        open={showITDocPopup}
        onClose={toggleITDocPopup}
        large
        showCloseButton={false}
      >
        <NonMonitoredAssetCreate
          toggleITDocPopup={toggleITDocPopup}
          entityName={props.assetClass?.entityName}
          classId={props.assetClass?.classId}
        />
      </Modal>
    </AssociatedAssetClassParent>
  );
}

export default withRouter(injectIntl(AssociatedAssetClass));
